import { FocusEvent, useCallback, useRef } from "react";
import { useIntl } from "react-intl";

declare global {
  interface Window {
    wmrk?: {
      Utils?: {
        addSkipMenu?: (event: Event) => void;
      };
    };
  }
}

declare const wmrk: {
  gotoMain?: () => void;
  gotoNav?: () => void;
};

interface SkipMenuProps {
  showNavigation?: boolean;
  showAiAssistant?: boolean;
}

const handleSkipToMain = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  wmrk?.gotoMain?.();
};

const handleSkipToNav = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  wmrk?.gotoNav?.();
};

const handleSkipToAiAssistant = (e: React.MouseEvent<HTMLAnchorElement>) => {
  e.preventDefault();
  window.requestAnimationFrame(() => {
    document.getElementById("ai-chat-toggle-btn")?.focus();
  });
};

export default function SkipMenu({ showNavigation = true, showAiAssistant = false }: SkipMenuProps): JSX.Element {
  if (!showNavigation && !showAiAssistant) {
    return (
      <section
        id="skip-to-menu"
        aria-label="Skip to main content"
      >
        <a
          className="skip-to"
          href="#maincontent"
          accessKey="0"
          onClick={handleSkipToMain}
        >
          Skip to Main Content
        </a>
      </section>
    );
  }

  if(showAiAssistant) {
    return <SkipMenuWithAiAssistant />;
  }
  return <SkipMenuWithDropdown />;
}

function SkipMenuWithDropdown(): JSX.Element {
  const intl = useIntl();
  const initializedRef = useRef(false);

  const initializeMenu = useCallback(
    (event: FocusEvent<HTMLAnchorElement>) => {
      if (initializedRef.current) {
        return;
      }

      const addSkipMenu = window?.wmrk?.Utils?.addSkipMenu;
      if (addSkipMenu) {
        addSkipMenu(event.nativeEvent);
        initializedRef.current = true;
      }
    },
    []
  );

  return (
    <section
      id="skip-to-menu"
      aria-label="Skip navigation shortcuts"
    >
      <a
        className="skip-to"
        href="#skip-menu-options"
        accessKey="0"
        role="button"
        aria-expanded="false"
        aria-controls="skip-menu-options"
        onFocus={initializeMenu}
      >
        Skip to ...
      </a>

      <ul id="skip-menu-options" aria-label="Skip navigation destinations">
        <li>
          <a
            className="skiplink"
            tabIndex={-1}
            href="#maincontent"
            onClick={handleSkipToMain}
            id="skip-1"
          >
            {intl.formatMessage({ id: "app.skipMenu.mainContentOption" })}
          </a>
        </li>
          <li>
            <a
              className="skiplink"
              tabIndex={-1}
              href="#navmenu"
              onClick={handleSkipToNav}
              id="skip-2"
            >
              {intl.formatMessage({ id: "app.skipMenu.navigationOption" })}
            </a>
          </li>
      </ul>
    </section>
  );
}
function SkipMenuWithAiAssistant(): JSX.Element {
  const intl = useIntl();
  const initializedRef = useRef(false);

  const initializeMenu = useCallback(
    (event: FocusEvent<HTMLAnchorElement>) => {
      if (initializedRef.current) {
        return;
      }

      const addSkipMenu = window?.wmrk?.Utils?.addSkipMenu;
      if (addSkipMenu) {
        addSkipMenu(event.nativeEvent);
        initializedRef.current = true;
      }
    },
    []
  );

  return (
    <section
      id="skip-to-menu"
      aria-label="Skip navigation shortcuts"
    >
      <a
        className="skip-to"
        href="#skip-menu-options"
        accessKey="0"
        role="button"
        aria-expanded="false"
        aria-controls="skip-menu-options"
        onFocus={initializeMenu}
      >
        Skip to ...
      </a>

      <ul id="skip-menu-options" aria-label="Skip navigation destinations">
        <li>
          <a
            className="skiplink"
            tabIndex={-1}
            href="#maincontent"
            onClick={handleSkipToMain}
            id="skip-1"
          >
            {intl.formatMessage({ id: "app.skipMenu.mainContentOption" })}
          </a>
        </li>
          <li>
            <a
              className="skiplink"
              tabIndex={-1}
              href="#ai-chat-toggle-btn"
              onClick={handleSkipToAiAssistant}
              id="skip-3"
            >
              {intl.formatMessage({ id: "app.skipMenu.aiAssistantOption" })}
            </a>
          </li>
      </ul>
    </section>
  );
}
